import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, Button, StatCard } from "@repo/o37-ui";
import { ArrowLeft, Edit, Trash2, Globe, Calendar, Users, DollarSign, CreditCard, Building, TrendingUp, TrendingDown, Target, Percent } from "lucide-react";
import { HoldingsCompany, HoldingsCompanyPerson, formatCents, HOLDINGS_COMPANY_STATUSES } from '../../types';
import CompanyForm from '../../components/Holdings/CompanyForm';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface CompanyCosts {
  subscriptions: any[];
  oneTimeCosts: any[];
  totalMonthlyCost: number;
  totalAnnualCost: number;
}

interface CompanyFinancials {
  income: number;
  expenses: number;
  net: number;
  expensesByCategory: Array<{ name: string; amount: number; children?: any[] }>;
  incomeByCategory: Array<{ name: string; amount: number; children?: any[] }>;
  transactionCount: number;
}

interface CompanyForecast {
  id: string;
  category: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  expectedCents: number;
  actualCents: number;
  notes?: string;
}

export default function CompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<HoldingsCompany | null>(null);
  const [costs, setCosts] = useState<CompanyCosts | null>(null);
  const [financials, setFinancials] = useState<CompanyFinancials | null>(null);
  const [forecasts, setForecasts] = useState<CompanyForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [activeTab, setActiveTab] = useState<'pl' | 'forecast'>('pl');

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    if (id) {
      loadCompany();
      loadCosts();
      loadFinancials();
      loadForecasts();
    }
  }, [id, selectedYear]);

  const loadCompany = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies/${id}`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompany({
          id: data.id,
          name: data.name,
          status: data.status,
          ownershipStakePercentage: data.ownership_stake_percentage,
          description: data.description,
          website: data.website,
          logoUrl: data.logo_url,
          industry: data.industry,
          foundedDate: data.founded_date,
          acquisitionDate: data.acquisition_date,
          exitDate: data.exit_date,
          notes: data.notes,
          createdBy: data.created_by,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          people: (data.holdings_company_people || []).map((p: any) => ({
            id: p.id,
            companyId: p.company_id,
            personId: p.person_id,
            roleAtCompany: p.role_at_company,
            startDate: p.start_date,
            endDate: p.end_date,
            isPrimaryContact: p.is_primary_contact,
            createdAt: p.created_at,
            person: p.holdings_people ? {
              id: p.holdings_people.id,
              name: p.holdings_people.name,
              email: p.holdings_people.email,
              role: p.holdings_people.role
            } : undefined
          }))
        });
      } else if (response.status === 404) {
        navigate('/holdings/portfolio');
      }
    } catch (e) {
      console.error('Failed to load company:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadCosts = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies/${id}/costs`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCosts({
          subscriptions: data.subscriptions || [],
          oneTimeCosts: data.oneTimeCosts || [],
          totalMonthlyCost: data.totalMonthlyCost || 0,
          totalAnnualCost: data.totalAnnualCost || 0
        });
      }
    } catch (e) {
      console.error('Failed to load costs:', e);
    }
  };

  const loadFinancials = async () => {
    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;
      const response = await fetch(
        `${API_URL}/holdings/companies/${id}/financials?startDate=${startDate}&endDate=${endDate}`,
        { headers: { 'x-user-id': userId, 'x-user-role': userRole } }
      );
      if (response.ok) {
        const data = await response.json();
        setFinancials({
          income: data.totalIncome || 0,
          expenses: data.totalExpenses || 0,
          net: (data.totalIncome || 0) - (data.totalExpenses || 0),
          expensesByCategory: data.expensesByCategory || [],
          incomeByCategory: data.incomeByCategory || [],
          transactionCount: data.transactionCount || 0
        });
      }
    } catch (e) {
      console.error('Failed to load financials:', e);
    }
  };

  const loadForecasts = async () => {
    try {
      const response = await fetch(
        `${API_URL}/holdings/revenue-forecasts?companyId=${id}`,
        { headers: { 'x-user-id': userId, 'x-user-role': userRole } }
      );
      if (response.ok) {
        const data = await response.json();
        setForecasts(data.map((f: any) => ({
          id: f.id,
          category: f.category,
          periodType: f.period_type,
          periodStart: f.period_start,
          periodEnd: f.period_end,
          expectedCents: f.expected_cents,
          actualCents: f.actual_cents || 0,
          notes: f.notes
        })));
      }
    } catch (e) {
      console.error('Failed to load forecasts:', e);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/holdings/companies/${id}`, {
        method: 'DELETE',
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        navigate('/holdings/portfolio');
      }
    } catch (e) {
      console.error('Failed to delete company:', e);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    const found = HOLDINGS_COMPANY_STATUSES.find(s => s.status === status);
    return found?.color || 'bg-neutral-100 text-neutral-500';
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Loading...</h1>
      </div>
    );
  }

  if (!company) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Company not found</h1>
        <Button onClick={() => navigate('/holdings/portfolio')} text="Back to Portfolio" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/holdings/portfolio')}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Portfolio</span>
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold">{company.name}</h1>
              <span className={`text-sm px-3 py-1 rounded-full font-medium ${getStatusColor(company.status)}`}>
                {company.status.charAt(0).toUpperCase() + company.status.slice(1)}
              </span>
            </div>
            {company.industry && (
              <p className="text-neutral-500">{company.industry}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowForm(true)}
              icon={<Edit className="w-4 h-4" />}
              text="Edit"
            />
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="text-red-600 border-red-200 hover:bg-red-50"
              icon={<Trash2 className="w-4 h-4" />}
              text={deleting ? 'Deleting...' : 'Delete'}
            />
          </div>
        </div>
      </div>

      {/* Stats Cards - Row 1: Company Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard
          label="Ownership"
          value={company.ownershipStakePercentage ? `${company.ownershipStakePercentage}%` : '-'}
          hint="Stake percentage"
        />
        <StatCard
          label="People"
          value={(company.people?.length || 0).toString()}
          hint="Assigned to company"
        />
        <StatCard
          label="Monthly Cost"
          value={costs ? formatCents(costs.totalMonthlyCost) : '-'}
          hint="From subscriptions"
        />
        <StatCard
          label="Annual Cost"
          value={costs ? formatCents(costs.totalAnnualCost) : '-'}
          hint="Total yearly"
        />
      </div>

      {/* Stats Cards - Row 2: Revenue & Margin */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="YTD Revenue"
          value={financials ? formatCents(financials.income) : '-'}
          hint={`${selectedYear} income`}
        />
        <StatCard
          label="YTD Expenses"
          value={financials ? formatCents(financials.expenses) : '-'}
          hint={`${selectedYear} costs`}
        />
        <StatCard
          label="Net Margin"
          value={financials ? formatCents(financials.net) : '-'}
          hint={financials && financials.net >= 0 ? 'Profitable' : 'Operating loss'}
        />
        <StatCard
          label="Margin %"
          value={financials && financials.income > 0
            ? `${((financials.net / financials.income) * 100).toFixed(1)}%`
            : '-'}
          hint="Net / Revenue"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Company Info */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Company Details</h2>

            {company.description && (
              <div className="mb-6">
                <p className="text-neutral-600">{company.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              {company.website && (
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Website</div>
                    <a
                      href={company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {new URL(company.website).hostname}
                    </a>
                  </div>
                </div>
              )}

              {company.acquisitionDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Acquired</div>
                    <div className="text-neutral-900">{formatDate(company.acquisitionDate)}</div>
                  </div>
                </div>
              )}

              {company.foundedDate && (
                <div className="flex items-start gap-3">
                  <Building className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Founded</div>
                    <div className="text-neutral-900">{formatDate(company.foundedDate)}</div>
                  </div>
                </div>
              )}

              {company.exitDate && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-neutral-400 mt-0.5" />
                  <div>
                    <div className="text-sm text-neutral-500">Exit Date</div>
                    <div className="text-neutral-900">{formatDate(company.exitDate)}</div>
                  </div>
                </div>
              )}
            </div>

            {company.notes && (
              <div className="mt-6 pt-6 border-t border-neutral-100">
                <div className="text-sm text-neutral-500 mb-2">Notes</div>
                <p className="text-neutral-600 whitespace-pre-wrap">{company.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* People */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">People</h2>
              <Link
                to="/holdings/people"
                className="text-sm text-blue-600 hover:underline"
              >
                View All
              </Link>
            </div>

            {company.people && company.people.length > 0 ? (
              <div className="space-y-3">
                {company.people.map((assignment: HoldingsCompanyPerson) => (
                  <div
                    key={assignment.id}
                    className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-neutral-200 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-neutral-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-neutral-900 truncate">
                        {assignment.person?.name || 'Unknown'}
                        {assignment.isPrimaryContact && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Primary
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-500 truncate">
                        {assignment.roleAtCompany || assignment.person?.role || '-'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>No people assigned</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Costs Section */}
      {costs && (costs.subscriptions.length > 0 || costs.oneTimeCosts.length > 0) && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Subscriptions */}
          {costs.subscriptions.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Subscriptions</h2>
                  <Link
                    to="/holdings/costs"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View All
                  </Link>
                </div>
                <div className="space-y-3">
                  {costs.subscriptions.slice(0, 5).map((sub: any) => (
                    <Link
                      key={sub.seat_id || sub.id}
                      to={`/holdings/subscriptions/${sub.id}`}
                      className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-4 h-4 text-neutral-400" />
                        <div>
                          <div className="font-medium text-neutral-900">{sub.vendor_name}</div>
                          <div className="text-sm text-neutral-500">
                            {sub.seats_assigned} seat{sub.seats_assigned !== 1 ? 's' : ''}
                            {sub.person && (
                              <span className="ml-1 text-neutral-400">
                                ({sub.person.name})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-neutral-900">
                          {formatCents(sub.monthly_cost || sub.cost_cents)}
                        </div>
                        <div className="text-xs text-neutral-500">/mo</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* One-Time Costs */}
          {costs.oneTimeCosts.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Recent One-Time Costs</h2>
                  <Link
                    to="/holdings/costs"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View All
                  </Link>
                </div>
                <div className="space-y-3">
                  {costs.oneTimeCosts.slice(0, 5).map((cost: any) => (
                    <div
                      key={cost.id}
                      className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-neutral-900">{cost.description}</div>
                        <div className="text-sm text-neutral-500">
                          {formatDate(cost.date)} - {cost.category}
                        </div>
                      </div>
                      <div className="font-medium text-neutral-900">
                        {formatCents(cost.amount_cents)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Financial Overview Section */}
      <div className="mt-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Financial Overview</h2>
              <div className="flex items-center gap-4">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-3 py-1.5 border border-neutral-200 rounded-lg text-sm"
                >
                  {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <Link
                  to={`/holdings/transactions?companyId=${id}`}
                  className="text-sm text-blue-600 hover:underline"
                >
                  View Transactions
                </Link>
              </div>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setActiveTab('pl')}
                className={`px-4 py-2 rounded-lg font-medium text-sm ${
                  activeTab === 'pl'
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Transaction P&L
              </button>
              <button
                onClick={() => setActiveTab('forecast')}
                className={`px-4 py-2 rounded-lg font-medium text-sm ${
                  activeTab === 'forecast'
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Forecast Tracking
              </button>
            </div>

            {/* Transaction P&L View */}
            {activeTab === 'pl' && (
              <div>
                {financials ? (
                  <>
                    {/* Summary Row */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="p-4 bg-emerald-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-700">Income</span>
                        </div>
                        <p className="text-xl font-semibold text-emerald-700">{formatCents(financials.income)}</p>
                      </div>
                      <div className="p-4 bg-red-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingDown className="w-4 h-4 text-red-600" />
                          <span className="text-sm text-red-700">Expenses</span>
                        </div>
                        <p className="text-xl font-semibold text-red-700">{formatCents(financials.expenses)}</p>
                      </div>
                      <div className={`p-4 rounded-xl ${financials.net >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Percent className="w-4 h-4" />
                          <span className={`text-sm ${financials.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Net</span>
                        </div>
                        <p className={`text-xl font-semibold ${financials.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCents(financials.net)}
                        </p>
                      </div>
                    </div>

                    {/* Category Breakdowns */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Income by Category */}
                      {financials.incomeByCategory.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-neutral-700 mb-3">Income by Category</h4>
                          <div className="space-y-2">
                            {financials.incomeByCategory.slice(0, 5).map((cat, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                                <span className="text-sm text-neutral-900">{cat.name}</span>
                                <span className="text-sm font-medium text-emerald-700">{formatCents(cat.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Expenses by Category */}
                      {financials.expensesByCategory.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-neutral-700 mb-3">Expenses by Category</h4>
                          <div className="space-y-2">
                            {financials.expensesByCategory.slice(0, 5).map((cat, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                                <span className="text-sm text-neutral-900">{cat.name}</span>
                                <span className="text-sm font-medium text-red-700">{formatCents(cat.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
                      Data from {financials.transactionCount} transactions in {selectedYear}
                    </div>
                  </>
                ) : (
                  <div className="py-8 text-center text-neutral-500">
                    <DollarSign className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
                    <p>No transaction data available</p>
                  </div>
                )}
              </div>
            )}

            {/* Forecast Tracking View */}
            {activeTab === 'forecast' && (
              <div>
                {forecasts.length > 0 ? (
                  <>
                    {/* Forecast Summary */}
                    {(() => {
                      const totalExpected = forecasts.reduce((sum, f) => sum + f.expectedCents, 0);
                      const totalActual = forecasts.reduce((sum, f) => sum + f.actualCents, 0);
                      const variance = totalActual - totalExpected;
                      const variancePct = totalExpected > 0 ? (variance / totalExpected) * 100 : 0;

                      return (
                        <div className="grid grid-cols-4 gap-4 mb-6">
                          <div className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 mb-1">Expected</p>
                            <p className="font-semibold">{formatCents(totalExpected)}</p>
                          </div>
                          <div className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 mb-1">Actual</p>
                            <p className="font-semibold text-emerald-600">{formatCents(totalActual)}</p>
                          </div>
                          <div className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 mb-1">Variance</p>
                            <p className={`font-semibold ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {variance >= 0 ? '+' : ''}{formatCents(variance)}
                            </p>
                          </div>
                          <div className="p-3 bg-neutral-50 rounded-lg">
                            <p className="text-xs text-neutral-500 mb-1">Performance</p>
                            <p className={`font-semibold ${variancePct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {variancePct >= 0 ? '+' : ''}{variancePct.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Forecast List */}
                    <div className="space-y-3">
                      {forecasts.map((forecast) => {
                        const variance = forecast.actualCents - forecast.expectedCents;
                        const pct = forecast.expectedCents > 0 ? (variance / forecast.expectedCents) * 100 : 0;
                        const progress = forecast.expectedCents > 0
                          ? Math.min(100, (forecast.actualCents / forecast.expectedCents) * 100)
                          : 0;

                        return (
                          <div key={forecast.id} className="p-4 bg-neutral-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-700">
                                  {forecast.category}
                                </span>
                                <span className="text-sm text-neutral-500">
                                  {new Date(forecast.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                              <span className={`text-sm font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {variance >= 0 ? '+' : ''}{pct.toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-neutral-500">Expected: {formatCents(forecast.expectedCents)}</span>
                              <span className="font-medium">Actual: {formatCents(forecast.actualCents)}</span>
                            </div>
                            <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  progress >= 100 ? 'bg-emerald-500' : progress >= 80 ? 'bg-emerald-400' : progress >= 50 ? 'bg-amber-400' : 'bg-red-400'
                                }`}
                                style={{ width: `${Math.min(100, progress)}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-4 text-center">
                      <Link to="/holdings/revenue/forecasts" className="text-sm text-blue-600 hover:underline">
                        Manage All Forecasts
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="py-8 text-center text-neutral-500">
                    <Target className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
                    <p className="mb-4">No revenue forecasts for this company</p>
                    <Link to="/holdings/revenue/forecasts" className="text-blue-600 hover:underline text-sm">
                      Create Forecast
                    </Link>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Company Form Modal */}
      {showForm && (
        <CompanyForm
          company={company}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            loadCompany();
          }}
        />
      )}
    </div>
  );
}
